import { isEmpty } from "lodash";

export default class MultiPageQuestions {

    //* DOM elements *//
    widgetContainer: HTMLDivElement;
    pageContainer: HTMLElement;
    optionRadioElements: NodeListOf<HTMLElement>;
    resultBodyContainer: HTMLElement;
    prevButton: HTMLButtonElement;
    nextButton: HTMLButtonElement;

    //* Widget specific elements *//
    pages: Map<number, string> = new Map();
    optionsByPage: Map<number, QuestionOption[]> = new Map();
    selectedOptions: Map<number, QuestionOption> = new Map();
    options: QuestionOption[];
    selectedOption: QuestionOption;

    currentPage: number = 1;
    hasNextPage: boolean = true;
    constructor(widgetId: string, initialOptions: QuestionOption[]) {
        this.widgetContainer = document.getElementById(widgetId) as HTMLDivElement;
        this.options = initialOptions;
        this.Init();
    }

    Init() {
        this.setElements();
        this.setInitialPage();
        this.setEventListeners();
    }

    private setElements() {
        this.pageContainer = this.widgetContainer.querySelector('.page');
        this.resultBodyContainer = this.widgetContainer.querySelector('.result-body');
        this.optionRadioElements = this.widgetContainer.querySelectorAll('.ktc-radio input');
        this.prevButton = this.widgetContainer.querySelector(".button--prev");
        this.nextButton = this.widgetContainer.querySelector(".button--next");
    }
    private setInitialPage() {
        const currentPageContent = this.widgetContainer.querySelector('.page[data-page="1"]').innerHTML;
        this.pages.set(1, currentPageContent);
        if (this.currentPage == 1 || this.hasNextPage) {
            this.nextButton.classList.add("show");
        }
        this.optionsByPage.set(1, this.options);
        const selectedOptionId = Array.from(this.optionRadioElements as NodeListOf<HTMLInputElement>).find(x => x.checked).id;
        this.selectedOption = this.options.find(x => x.OptionGUID == selectedOptionId);
        this.selectedOptions.set(1, this.selectedOption);
        this.updateNextPage();
    }
    private setEventListeners() {
        this.prevButton.addEventListener("click", () => this.goToPreviousPage());
        this.nextButton.addEventListener("click", () => this.goToNextPage())
        this.pageContainer.addEventListener("change", (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target && target.type === "radio") {
                this.updateSelectedOption(target);
            }
        });
    }
    private updateNextPage(option?: QuestionOption) {
        const selectedOption = option ?? this.selectedOption;
        let nextPageContent = '';
        if (selectedOption?.NestedOptions && selectedOption?.NestedOptions.length > 0) {
            this.hasNextPage = true;
            nextPageContent = `
                <legend class="question-text h3">
                    <span class="visually-hidden">Question: </span>
                    <span class="prefix" aria-hidden="true">Q: </span>
                    <span class="value" id="question-header">${selectedOption.Question}</span>
                </legend>
                <div class="form-field">
                    ${this.createRadioOptions(selectedOption.NestedOptions)}
                </div>`
            this.pageContainer.setAttribute('role', 'radiogroup');
        } else {
            this.hasNextPage = false;
            nextPageContent = `
                ${selectedOption.Results}
                ${selectedOption.RelatedLinkButton?.ButtonText != null ? this.createCTAButton(selectedOption.RelatedLinkButton) : ""}
                `
            this.pageContainer.removeAttribute('role');
        }
        this.pages.set(this.currentPage + 1, nextPageContent);
    }
    private updateSelectedOption(target: HTMLInputElement) {
        if (target.checked) {
            const id = target.id;
            this.selectedOption = this.options.find(x => x.OptionGUID == id);
            this.updateNextPage();
            this.selectedOptions.set(this.currentPage, this.selectedOption);
        }
    }
    private createRadioOptions(options: QuestionOption[]) {
        return options.map((option, i) => (
            `<span class="ktc-radio">
                <input class="form-control" ${i == 0 ? 'focus checked' : ''} required id="${option.OptionGUID}" name="question-${this.currentPage + 1}" type="radio" value="${option.OptionGUID}">
                <label for="${option.OptionGUID}">${option.OptionText}</label>
            </span>`
        )).join("");
    }
    private goToNextPage() {
        this.clearErrorMessage();
        this.currentPage++;
        this.updatePageContent();
        if (this.selectedOption.NestedOptions && this.selectedOption.NestedOptions.length > 0) {
            this.optionsByPage.set(2, this.selectedOption.NestedOptions);
            this.options = this.selectedOption.NestedOptions
        }
        this.updateButtons();
        this.focusFirstFocusableElement();
    }
    private goToPreviousPage() {
        this.currentPage--;
        this.hasNextPage = true;
        this.updatePageContent();
        this.updateButtons();
        this.options = this.optionsByPage.get(this.currentPage);
        this.restorePreviousSelection();
        this.selectedOption = this.selectedOptions.get(this.currentPage);
        this.updateNextPage();
    }
    private updatePageContent() {
        if (this.hasNextPage) {
            this.hideResult();
            this.pageContainer.innerHTML = this.pages.get(this.currentPage);
            this.pageContainer.setAttribute("data-page", this.currentPage.toString());
        } else {
            this.displayResult();
        }
    }
    private updateButtons() {
        if (this.currentPage == 1) {
            this.prevButton.classList.remove("show");
            this.nextButton.classList.add("show");
        }
        else if (this.hasNextPage) {
            this.nextButton.classList.add("show");
            this.prevButton.classList.add("show");
        } else {
            this.nextButton.classList.remove("show");
            this.prevButton.classList.add("show");
        }
    }
    private restorePreviousSelection() {
        const previouslySelectedOption = this.selectedOptions.get(this.currentPage);

        if (previouslySelectedOption) {
            const input = this.pageContainer.querySelector(`input[id="${previouslySelectedOption.OptionGUID}"]`) as HTMLInputElement;
            if (input) {
                input.checked = true;
            }
        }
    }
    private createCTAButton(buttonProps: Button) {
        const buttonColour = !isEmpty(buttonProps.ButtonColour) && buttonProps.ButtonColour.split(".").length > 1
            ? (buttonProps.ButtonColour.split(".")[1] === "Primary" ? "colour--dark" : "colour--light")
            : "";
        const combinedClass = `${buttonProps.ButtonStyle.toLowerCase()} ${buttonColour} ${buttonProps.ButtonIconPosition} ${buttonProps.ButtonSize}`
        return `
        <a href="${buttonProps.ButtonLinkUrl}" class="button ${combinedClass}" tabindex="0">
            <span class="button-icon ${buttonProps.ButtonIcon}"></span>
            <span class="button-content">${buttonProps.ButtonText}</span>
        </a>
        `
    }
    private clearErrorMessage() {
        const errorMessageEl = this.widgetContainer.querySelector(".error");
        errorMessageEl?.remove();
    }
    private focusFirstFocusableElement() {
        const firstRadio = this.pageContainer.querySelector('input[type="radio"]') as HTMLInputElement;
        if (firstRadio) {
            firstRadio.focus();
            firstRadio.checked = true;
            this.updateSelectedOption(firstRadio);
        }
    }
    private displayResult() {
        this.pageContainer.classList.add('d-none');
        this.resultBodyContainer.classList.remove('d-none');
        setTimeout(() => {
            this.resultBodyContainer.innerHTML ="<p class='visually-hidden'>Results: </p>" + this.pages.get(this.currentPage);
        }, 10)
        this.resultBodyContainer.focus();
    }
    private hideResult() {
        this.pageContainer.classList.remove('d-none');
        this.resultBodyContainer.classList.add('d-none');
    }
}